import { useEffect, useMemo, useRef } from 'react';

import { useAuthUser } from '@/hooks/useAuthUser';
import { useLibrary } from '@/hooks/useLibrary';
import { getGenerationEngine } from '@/services/generation';
import { getAppStore } from '@/store';
import type { GenerationRequest, PendingGeneration } from '@/types';
import showNotification, { NotificationType } from '@/utils/showNotification';

function buildRequest(p: PendingGeneration): GenerationRequest {
  return {
    character: p.snapshot.character,
    wardrobeItems: p.snapshot.selectedItems,
    viewMode: p.viewMode,
    lookFolder: p.lookFolder,
  };
}

/**
 * On mount, scans persisted pending generations: drops stale ones (TTL),
 * and resumes polling for the rest. Successful resumes feed back into
 * generationSuccess + saveLookToLibrary, mirroring the live-generation path.
 */
export function useResumePendingGenerations() {
  const { saveLookToLibrary } = useLibrary();
  const { isAuthorized } = useAuthUser();
  const engine = useMemo(() => getGenerationEngine(), []);
  const startedRef = useRef(false);

  useEffect(() => {
    // Wait for auth — resuming under a transient unauthenticated cold start
    // would 401 and silently drop the pending job before the session restored.
    if (!isAuthorized) return;
    if (startedRef.current) return;
    startedRef.current = true;

    const store = getAppStore();
    const resumable = store.pruneAndCollectResumablePendings();
    if (resumable.length === 0) return;

    // Restore the snapshot of the most-recent pending so the canvas shows the
    // character + selections under the generating overlay (matches live behavior).
    const mostRecent = resumable.reduce((a, b) => (a.timestamp >= b.timestamp ? a : b));
    store.viewPendingGeneration(mostRecent.id);

    for (const pending of resumable) {
      const request = buildRequest(pending);
      let job;
      if (pending.executionId) {
        job = engine.resumeExecution(pending.executionId, request);
      } else {
        console.warn('[useResumePendingGenerations] Restarting orphan pending', { id: pending.id, age: Date.now() - pending.timestamp });
        job = engine.generate(request, {
          onSubmitted: executionId => getAppStore().setPendingExecutionId(pending.id, executionId),
        });
      }

      job
        .then(result => {
          getAppStore().removePendingGeneration(pending.id);
          getAppStore().generationSuccess(result, undefined, pending.characterId || undefined);
          if (pending.characterImageUrl) {
            saveLookToLibrary(result, pending.characterId, pending.characterImageUrl, pending.gender);
          }
          // Intentionally no success toast on resume. The canvas updates with
          // the result, which is its own visible confirmation; firing a toast
          // here also fires it for stale orphans the user never knowingly
          // submitted (e.g. a job that completed server-side after the tab
          // closed and got resurrected on next page load).
        })
        .catch(err => {
          console.error('[useResumePendingGenerations] Resume failed', { id: pending.id, err });
          getAppStore().removePendingGeneration(pending.id);
          getAppStore().generationError(err instanceof Error ? err.message : 'Generation failed');
          showNotification(NotificationType.generationError);
        });
    }
  }, [engine, saveLookToLibrary, isAuthorized]);
}
